// src/components/BoardPack/pages/consolidate_boardpack/Business_Performance_Page.jsx
import React, { useEffect, useRef, useState } from "react";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import { dynamicKey } from "../../utils/dynamicKey";
import Business_Performance_component from "../../components/consolidate_boardpack_components/Business_Performance_component";

// Top summary block ("ALL COMPANIES") — no contribution columns.
// The API doesn't send styling flags, so derive them from the label
// (matches the reference report: EBITDA / Operating Profit rows are
// shaded, margin rows are bold red).
const SILVER_LABELS = ["EBITDA", "OPERATING PROFIT/(LOSS)", "EBITDA Margin", "NET PROFIT MARGIN"];
const RED_LABEL_ROWS = ["EBITDA Margin", "NET PROFIT MARGIN"];
const BOLD_LABELS = ["OPERATING PROFIT/(LOSS)", "NET PROFIT MARGIN"];

const buildSummaryRows = (block) => {
  if (!block?.rows) return [];

  return block.rows.map((r) => ({
    label: r.label,
    values: r.values || [],
    highlight: r.isGrandTotal || SILVER_LABELS.includes(r.label),
    bold: BOLD_LABELS.includes(r.label),
    redLabel: RED_LABEL_ROWS.includes(r.label) || r.isPercent,
  }));
};

// Bottom detail block ("Mawgif Group SAR'000 - Revenue") — same shape as
// the EBITDA / Net Profit tables (contribution columns + GRAND TOTAL row).
const buildDetailRows = (block) => {
  if (!block?.rows) return [];

  return block.rows.map((r) => ({
    label: r.label,
    contribution1: r.contributionMonth,
    contribution2: r.contributionYtd,
    block1: (r.values || []).slice(0, 4),
    block2: (r.values || []).slice(4, 8),
    ytd1: (r.values || []).slice(8, 12),
    ytd2: (r.values || []).slice(12, 16),
    isGrandTotal: !!r.isGrandTotal,
  }));
};

const Business_Performance = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [meta, setMeta] = useState(null);
  const hasFetchedRef = useRef(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, {
        year,
        month,
        contract,
        measure: 3,
        currency: "Reporting Currency",
        is_cfo: 0,
      });

      const response = await enqueue(() =>
        apiCall("GET", "/boardpack/consolidate", apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      setTableData(response.data);
      setMeta(response.meta);
    } catch (err) {
      console.error("Business Performance fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  // Real API shape: headers live at data-level (shared by both blocks),
  // the summary block is "top", the detail block is "segment".
  const summary = tableData?.top; // "ALL COMPANIES" block
  const detail = tableData?.segment; // "Mawgif Group - Revenue" block
  const headerYears = tableData?.headerYears;
  const headerMonths = tableData?.headerMonths;
  const headerMeasures = tableData?.headerMeasures;

  return (
    <Business_Performance_component
      reportTitle={dynamicKey({ text: page?.title, year, month, contract })}
      sectionTitle={dynamicKey({ text: page?.subtitle, year, month, contract })}
      logoSrc={meta?.logoUrl}
      summaryBannerLabel={page?.line2 || ""}
      summaryCornerLabel={page?.line1 || ""}
      summaryRows={buildSummaryRows(summary)}
      summaryHeaderYears={headerYears}
      summaryHeaderMonths={headerMonths}
      summaryHeaderMeasures={headerMeasures}
      detailTitle={page?.line3 || ""}
      detailLogoSrc={meta?.logoUrl}
      detailRows={buildDetailRows(detail)}
      detailHeaderYears={headerYears}
      detailHeaderMonths={headerMonths}
      detailHeaderMeasures={headerMeasures}
    />
  );
};

export default Business_Performance;